import React, { useState, FC, useEffect, Suspense, useContext } from 'react';
import { Box, Text, ToggleSwitch } from '@bechtel/gds';
import { Trans } from '@lingui/macro';
import { CaddyDetailItem } from './item';
import { CaddyWelderDetailProps } from './types';
import { useName, ErrorBoundary, SuspenseLoader } from '@bechtel/client-common';
import { filter, find, findIndex, map, some, every, includes, toLower, flatMap, uniqBy, get } from 'lodash';
import { CrewMember, ReturnCaddiesError, RodRequestCaddy, RodRequestCaddyStatusCode } from '@bechtel/types';
import ReturnCaddyConfirm from './return-caddy-confirm/internals';
import { BulkReturnCaddyError } from '~/request/bulk-return/error';
import { useReturnCaddy } from '~/hooks';
import { useRodRoomPermissions } from '@bechtel/client-weld-common';
import { ApolloError } from '@apollo/client';
import { RodRoomDataContext } from '~/utilities/context';

export const CaddyWelderDetail: FC<CaddyWelderDetailProps> = ({ caddies, welders, searchQuery }) => {
  const { getFullName } = useName();
  const [returnCaddiesList, setReturnCaddiesList] = useState<RodRequestCaddy[]>([]);
  const [returnCaddiesError, setReturnCaddiesError] = useState<ReturnCaddiesError[] | ApolloError | null>(null);
  const { selectedRodRoom } = useContext(RodRoomDataContext);
  const { canReturnCaddies } = useRodRoomPermissions();

  const getCaddiesCount = (issuedTo, caddyStatus) => {
    if (caddyStatus === RodRequestCaddyStatusCode.RETURNED) {
      return filter(
        caddies,
        (caddy) =>
          caddy.currentStatusI18n == RodRequestCaddyStatusCode.RETURNED &&
          findIndex(caddy.stencils, (stencil) => stencil.id === issuedTo) !== -1
      ).length;
    } else {
      return filter(
        caddies,
        (caddy) =>
          (caddy.currentStatusI18n == RodRequestCaddyStatusCode.RETURNED ||
            caddy.currentStatusI18n == RodRequestCaddyStatusCode.ISSUED) &&
          findIndex(caddy.stencils, (stencil) => stencil.id === issuedTo) !== -1
      ).length;
    }
  };

  const searchCaddies = filter(caddies, (caddy) => {
    const searchValue = toLower(searchQuery);
    const caddyLabel = toLower(caddy.caddy.label);
    const firstName = toLower(map(caddy.stencils, 'firstName').join(','));
    const lastName = toLower(map(caddy.stencils, 'lastName').join(','));
    return includes(caddyLabel, searchValue) || includes(firstName, searchValue) || includes(lastName, searchValue);
  });

  const filteredWelders: CrewMember[] = uniqBy(flatMap(caddies, 'stencils'), 'id');

  const welderCaddies: { welder: CrewMember; caddies: RodRequestCaddy[] }[] = map(filteredWelders, (welder) => {
    const filteredCaddies: RodRequestCaddy[] = [];
    map(searchCaddies.length ? searchCaddies : caddies, (caddy) => {
      if (some(caddy.stencils, (stencil) => stencil.id === welder.id)) {
        filteredCaddies.push(caddy);
      }
    });
    return {
      welder,
      caddies: filteredCaddies,
    };
  });

  const checkAvailableCaddies = (caddyData: RodRequestCaddy[]) => {
    return every(caddyData, (caddy) => includes(returnCaddiesList, caddy));
  };

  const handleReturnAll = (welderId: string) => {
    const selectedCaddies: RodRequestCaddy[] = find(welderCaddies, (welderCaddy) => welderCaddy.welder.id === welderId)
      ?.caddies;
    if (checkAvailableCaddies(selectedCaddies)) {
      setReturnCaddiesList([]);
    } else {
      setReturnCaddiesList(selectedCaddies);
    }
  };

  const onReturnCaddiesComplete = () => {
    setReturnCaddiesList([]);
  };

  const [
    returnCaddies,
    { loading: returnCaddyLoading, error: returnCaddyMutationError, data: returnCaddyData },
  ] = useReturnCaddy(onReturnCaddiesComplete);

  const bulkReturnCaddyError: ReturnCaddiesError[] = get(returnCaddyData, 'returnCaddies.erroredCaddies', []);

  const allCaddiesReturned = (caddies: RodRequestCaddy[]) => {
    return (
      caddies.length ===
      filter(caddies, (caddy) => caddy.currentStatusI18n === RodRequestCaddyStatusCode.RETURNED)?.length
    );
  };

  useEffect(() => {
    if (bulkReturnCaddyError.length < 1) return;
    setReturnCaddiesError(bulkReturnCaddyError);
  }, [bulkReturnCaddyError]);

  useEffect(() => {
    if (!returnCaddyMutationError) return;
    setReturnCaddiesError(returnCaddyMutationError);
  }, [returnCaddyMutationError]);

  return map(welderCaddies, (welderCaddy, index) => (
    <Box
      mt={{ sm: '500' }}
      key={welderCaddy.welder.id}
      flexDirection={{ sm: 'column' }}
      data-testid={`caddy-welder-detail-container-${index}`}
    >
      <Box
        alignItems={{ sm: 'center' }}
        justifyContent={{ sm: 'space-between' }}
        flexGrow={{ sm: 1 }}
        css={`
          ${({ theme }) => `background: ${theme.colors.grey[100]}; border-radius: ${theme.spacings[200]}`}
        `}
        px={{ sm: '450' }}
        py={{ sm: '300' }}
      >
        {canReturnCaddies && (
          <Box alignItems={{ sm: 'center' }} data-testid={`return-all-toggle-${index}`}>
            <ToggleSwitch
              isToggled={checkAvailableCaddies(welderCaddy?.caddies) || allCaddiesReturned(welderCaddy?.caddies)}
              onToggle={() => handleReturnAll(welderCaddy.welder?.id)}
              disabled={allCaddiesReturned(welderCaddy?.caddies)}
            />
            &nbsp;&nbsp;
            <Text size="300" color="bechtel.blue.700" weight="400">
              <Trans>Return All</Trans>
            </Text>
          </Box>
        )}
        <Text
          size="400"
          color="grey.700"
          weight="400"
          title={getFullName(
            welderCaddy.welder?.firstName ? welderCaddy.welder.firstName : ' ',
            welderCaddy.welder?.lastName ? welderCaddy.welder.lastName : ' '
          )}
          data-testid={`welder-name-${index}`}
        >
          {getFullName(
            welderCaddy.welder?.firstName ? welderCaddy.welder.firstName : ' ',
            welderCaddy.welder?.lastName ? welderCaddy.welder.lastName : ' '
          )}
          &nbsp; ({getCaddiesCount(welderCaddy.welder?.id, RodRequestCaddyStatusCode.RETURNED)}/
          {getCaddiesCount(welderCaddy.welder?.id, RodRequestCaddyStatusCode.ISSUED)})
        </Text>
      </Box>
      {map(welderCaddy.caddies, (caddy) => (
        <CaddyDetailItem
          caddy={caddy}
          welders={welders}
          returnCaddies={flatMap(returnCaddiesList, 'caddy')}
          handleReturnCaddies={(caddyData) => {
            setReturnCaddiesList(filter(welderCaddy.caddies, (caddyItem) => includes(caddyData, caddyItem.caddy)));
          }}
        />
      ))}

      {returnCaddiesList.length > 0 && (
        <ReturnCaddyConfirm
          caddies={flatMap(
            filter(returnCaddiesList, (caddy) => caddy.currentStatusI18n !== RodRequestCaddyStatusCode.RETURNED),
            'caddy'
          )}
          loading={returnCaddyLoading}
          onCancel={() => setReturnCaddiesList([])}
          onConfirm={async () =>
            await returnCaddies({
              variables: {
                input: {
                  ids: flatMap(
                    filter(
                      returnCaddiesList,
                      (caddy) => caddy.currentStatusI18n !== RodRequestCaddyStatusCode.RETURNED
                    ),
                    'id'
                  ),
                  returnedToRodRoomId: selectedRodRoom?.id,
                },
              },
            })
          }
        />
      )}
      {(returnCaddiesError instanceof ApolloError || returnCaddiesError instanceof Array) && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <BulkReturnCaddyError errors={returnCaddiesError} close={() => setReturnCaddiesError(null)} />
          </Suspense>
        </ErrorBoundary>
      )}
    </Box>
  ));
};
